import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { blogService } from '../services/blogService';
import { db } from '../utils/firebaseConfig';
import { LoadingSpinner } from '../components/ui';
import { useAuth } from '../hooks/useAuth';
import type { BlogPost } from '../types/blog';
import type { Event } from '../services/eventService';

export function BlogViewPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [blog, setBlog] = useState<BlogPost | null>(null);
  const [featuredEvents, setFeaturedEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [publishing, setPublishing] = useState(false);

  useEffect(() => {
    const loadBlog = async () => {
      if (!id) {
        setError('Blog post not found');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const blogData = await blogService.getBlogPost(id);

        if (!blogData) {
          setError('Blog post not found');
          setLoading(false);
          return;
        }

        // Only show published posts to non-admin users
        if (blogData.status !== 'published' && !isAdmin) {
          setError('This blog post is not published yet');
          setLoading(false);
          return;
        }

        setBlog(blogData);

        // Increment view count (only for published posts)
        // Wrapped in try-catch since only admins have write permission
        if (blogData.status === 'published') {
          try {
            await blogService.incrementViewCount(id);
          } catch {
            // Silently fail - view count tracking requires admin permission
          }
        }

        // Load featured events
        if (blogData.featuredEvents.length > 0) {
          const eventsPromises = blogData.featuredEvents.map(async (eventId) => {
            const eventDoc = await getDoc(doc(db, 'events', eventId));
            if (eventDoc.exists()) {
              return { id: eventDoc.id, ...eventDoc.data() } as Event;
            }
            return null;
          });

          const events = await Promise.all(eventsPromises);
          setFeaturedEvents(events.filter((e) => e !== null) as Event[]);
        }
      } catch (err) {
        console.error('Error loading blog:', err);
        setError('Failed to load blog post');
      } finally {
        setLoading(false);
      }
    };

    loadBlog();
  }, [id, isAdmin]);

  const handlePublish = async () => {
    if (!id || !blog) return;

    try {
      setPublishing(true);
      await blogService.updateBlogPost(id, { status: 'published' });
      setBlog({ ...blog, status: 'published' });
    } catch (err) {
      console.error('Error publishing blog:', err);
      alert('Failed to publish blog post. Please try again.');
    } finally {
      setPublishing(false);
    }
  };

  // Simple markdown-to-HTML converter (basic implementation)
  const renderMarkdown = (text: string) => {
    let html = text;

    // Headers
    html = html.replace(/^### (.*$)/gim, '<h3 class="text-xl font-bold text-gray-900 mt-6 mb-3">$1</h3>');
    html = html.replace(/^## (.*$)/gim, '<h2 class="text-2xl font-bold text-gray-900 mt-8 mb-4">$1</h2>');
    html = html.replace(/^# (.*$)/gim, '<h1 class="text-3xl font-bold text-gray-900 mt-8 mb-4">$1</h1>');

    // Bold
    html = html.replace(/\*\*(.+?)\*\*/g, '<strong class="font-semibold">$1</strong>');

    // Italic
    html = html.replace(/\*(.+?)\*/g, '<em class="italic">$1</em>');

    // Links
    html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" class="text-primary-600 hover:text-primary-700 underline" target="_blank" rel="noopener noreferrer">$1</a>');

    // Line breaks
    html = html.replace(/\n\n/g, '</p><p class="text-gray-700 leading-relaxed mb-4">');
    html = html.replace(/\n/g, '<br />');

    return `<p class="text-gray-700 leading-relaxed mb-4">${html}</p>`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center space-y-4">
          <LoadingSpinner size="lg" />
          <p className="text-gray-600">Loading blog post...</p>
        </div>
      </div>
    );
  }

  if (error || !blog) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Error</h2>
          <p className="text-gray-600 mb-6">{error || 'Blog post not found'}</p>
          <button onClick={() => navigate('/')} className="btn-primary">
            Go Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Draft Banner for Admins */}
      {isAdmin && blog.status === 'draft' && (
        <div className="bg-yellow-50 border-b border-yellow-200">
          <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="px-2 py-1 bg-yellow-200 text-yellow-800 rounded-full text-xs font-medium">
                Draft
              </span>
              <span className="text-yellow-800 text-sm">
                This blog post is not published yet. Only admins can see this preview.
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Link
                to={`/admin/blog/edit/${id}`}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm font-medium flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                Edit
              </Link>
              <button
                onClick={handlePublish}
                disabled={publishing}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {publishing ? (
                  <>
                    <LoadingSpinner size="sm" />
                    Publishing...
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Publish Now
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <article className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <Link to="/" className="text-primary-600 hover:text-primary-700 text-sm font-medium mb-4 inline-block">
            ← Back to Events
          </Link>

          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">{blog.title}</h1>

          <div className="flex items-center gap-4 text-sm text-gray-600 mb-6">
            <div className="flex items-center gap-2">
              {blog.author.avatar && (
                <img src={blog.author.avatar} alt={blog.author.name} className="w-8 h-8 rounded-full" referrerPolicy="no-referrer" />
              )}
              <span className="font-medium text-gray-900">{blog.author.name}</span>
            </div>
            <span>•</span>
            <time>{blog.status === 'published' && blog.publishedAt ? blog.publishedAt.toDate().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : 'Not published'}</time>
            <span>•</span>
            <span>{blog.viewCount} views</span>
          </div>

          {/* Tags */}
          {blog.tags.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-6">
              {blog.tags.map((tag) => (
                <span key={tag} className="px-3 py-1 bg-primary-100 text-primary-800 rounded-full text-sm">
                  {tag}
                </span>
              ))}
            </div>
          )}

          {/* Excerpt */}
          <p className="text-xl text-gray-600 leading-relaxed">{blog.excerpt}</p>
        </div>

        {/* Featured Image */}
        {blog.featuredImage && (
          <div className="mb-8 rounded-2xl overflow-hidden">
            <img src={blog.featuredImage} alt={blog.title} className="w-full h-auto object-cover" referrerPolicy="no-referrer" />
          </div>
        )}

        {/* Content */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 mb-8">
          <div className="prose prose-lg max-w-none" dangerouslySetInnerHTML={{ __html: renderMarkdown(blog.content) }} />
        </div>

        {/* Featured Events */}
        {featuredEvents.length > 0 && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Featured Events</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {featuredEvents.map((event) => (
                <Link
                  key={event.id}
                  to={`/events/${event.id}`}
                  className="group border border-gray-200 rounded-xl overflow-hidden hover:shadow-lg transition-shadow"
                >
                  {(event.image || event.linkPreview?.image) && (
                    <div className="aspect-video bg-gray-200 overflow-hidden">
                      <img src={event.image || event.linkPreview?.image || ''} alt={event.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform" referrerPolicy="no-referrer" />
                    </div>
                  )}
                  <div className="p-4">
                    <h3 className="font-semibold text-gray-900 mb-2 group-hover:text-primary-600 transition-colors">{event.title}</h3>
                    <div className="text-sm text-gray-600 space-y-1">
                      <p>
                        📅 {event.startDate}
                        {event.startDate !== event.endDate && ` - ${event.endDate}`}
                      </p>
                      <p>📍 {event.location?.city || event.city?.name || 'Location TBD'}</p>
                      {event.isFree ? (
                        <p className="text-green-600 font-medium">Free Event</p>
                      ) : (
                        <p className="text-gray-900 font-medium">${event.cost}</p>
                      )}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Share Section */}
        <div className="mt-8 bg-primary-50 rounded-2xl p-6 text-center">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Share this blog post</h3>
          <p className="text-gray-600 mb-4">Copy the link below to share with your friends</p>
          <div className="flex gap-2 max-w-2xl mx-auto">
            <input
              type="text"
              value={window.location.href}
              readOnly
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg bg-white text-gray-700 text-sm"
              onClick={(e) => e.currentTarget.select()}
            />
            <button
              onClick={() => {
                navigator.clipboard.writeText(window.location.href);
                alert('Link copied to clipboard!');
              }}
              className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm font-medium"
            >
              Copy
            </button>
          </div>
        </div>
      </article>
    </div>
  );
}
